import { useEffect } from "react"
import type { Audio, AudioType } from "../../../../types/scheduled-post"
import type { AudioSearchState } from "../../../../hooks/useAudioSearch"
import type { AudioPlayerState } from "../../../../hooks/useAudioPlayer"
import { formatAudioDuration } from "../../../../lib/audioDuration"

const TABS: { value: AudioType; label: string }[] = [
  { value: "music",           label: "音楽" },
  { value: "original_sound",  label: "オリジナル音源" },
]

type Props = {
  search: AudioSearchState
  player: AudioPlayerState
  onSelect: (audio: Audio) => void
  onClose: () => void
}

// 音源選択モーダル。タブ（音楽 / オリジナル音源）＋検索欄＋結果リスト＋試聴。
// 検索・プレイヤーの state は AudioSection から props で受け取る（閉じても結果を保持するため）。
const AudioPickerModal = ({ search, player, onSelect, onClose }: Props) => {
  const {
    audioType, query, setQuery, results, loading, error, searched,
    search: runSearch, selectAudioType, loadTrendingIfNeeded,
  } = search

  // 初回表示はトレンド音源（クエリなしの 1 コール）。既に検索済みなら何もしない
  useEffect(() => { loadTrendingIfNeeded() }, [loadTrendingIfNeeded])

  // モーダルを閉じたら試聴を止める
  const { stop: stopPreview } = player
  useEffect(() => () => { stopPreview() }, [stopPreview])

  return (
    <div className="audio-picker" onClick={onClose}>
      <div className="audio-picker__inner" onClick={e => e.stopPropagation()}>
        <div className="audio-picker__header">
          <span className="audio-picker__title">音源を選ぶ</span>
          <button className="audio-picker__close" type="button" onClick={onClose} aria-label="閉じる">
            <i className="bi bi-x-lg" />
          </button>
        </div>

        <div className="audio-picker__tabs" role="tablist">
          {TABS.map(tab => (
            <button
              key={tab.value}
              type="button"
              role="tab"
              aria-selected={audioType === tab.value}
              className={`audio-picker__tab${audioType === tab.value ? " audio-picker__tab--active" : ""}`}
              disabled={loading}
              onClick={() => { if (audioType !== tab.value) selectAudioType(tab.value) }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className="audio-picker__search">
          <input
            type="text"
            className="form-input"
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={e => {
              if (e.key !== "Enter") return
              e.preventDefault()
              if (!loading) runSearch()
            }}
            placeholder="曲名・アーティスト名で検索"
          />
          <button
            type="button"
            className="ps-btn ps-btn--primary"
            onClick={() => runSearch()}
            disabled={loading}
          >
            <i className="bi bi-search" /> 検索
          </button>
        </div>

        <div className="audio-picker__body">
          {loading ? (
            <div className="audio-picker__status">
              <span className="spinner" /> 検索中...
            </div>
          ) : error ? (
            <div className="media-errors__item">
              <i className="bi bi-exclamation-circle" />{error}
            </div>
          ) : results.length === 0 ? (
            <div className="audio-picker__status">
              {searched ? "該当する音源が見つかりませんでした" : "検索すると音源が表示されます"}
            </div>
          ) : (
            <ul className="audio-picker__list">
              {results.map(audio => {
                const duration   = formatAudioDuration(audio.duration_in_ms)
                const isPlaying  = player.playingId === audio.audio_id
                const artist     = audio.audio_type === "original_sound" && audio.artist
                  ? `@${audio.artist}`
                  : audio.artist

                return (
                  <li className="audio-picker__item" key={audio.audio_id}>
                    <div className="audio-artwork">
                      {audio.cover_artwork_url ? (
                        <img className="audio-artwork__img" src={audio.cover_artwork_url} alt="" />
                      ) : (
                        <i className="bi bi-music-note-beamed" />
                      )}
                    </div>

                    <div className="audio-picker__meta">
                      <div className="audio-picker__item-title">{audio.title || "タイトルなし"}</div>
                      <div className="audio-picker__item-sub">
                        {artist && <span>{artist}</span>}
                        {duration && <span>{duration}</span>}
                      </div>
                    </div>

                    {/* 再生ボタン・外部リンク・「試聴不可」は別要素なので、
                        同じスロットに入れて列を揃える（幅は SCSS 側で担保する）。
                        instagram_url は音声ファイルではなく Web ページなのでインライン再生
                        できない。ここだけは 44px スロットに収めるためアイコンのみで出し、
                        文言は aria-label / title で補う（理由は SCSS 側のコメント参照） */}
                    <div className="audio-picker__preview">
                      {audio.preview_url ? (
                        <button
                          type="button"
                          className="audio-preview-btn"
                          onClick={() => player.toggle(audio.audio_id, audio.preview_url)}
                          aria-label={isPlaying ? "試聴を停止" : "試聴する"}
                        >
                          <i className={isPlaying ? "bi bi-pause-fill" : "bi bi-play-fill"} />
                        </button>
                      ) : audio.instagram_url ? (
                        <a
                          className="audio-external-btn"
                          href={audio.instagram_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label="Instagram で聴く"
                          title="Instagram で聴く"
                        >
                          <i className="bi bi-instagram" />
                        </a>
                      ) : (
                        <span className="audio-picker__no-preview">試聴不可</span>
                      )}
                    </div>

                    <button
                      type="button"
                      className="ps-btn ps-btn--primary audio-picker__select"
                      onClick={() => onSelect(audio)}
                    >
                      選択
                    </button>
                  </li>
                )
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  )
}

export default AudioPickerModal
